// Deferred transcribe-mode choices for server sessions toggled while offline
// (APP-7994).
//
// When the app is offline and the user flips Transcribe/Dictate on a *server*
// session (not a local offline recording), the mutation can't PATCH the backend
// and the optimistic GET_SESSION_BY_ID cache write has no server backing — the
// next successful refetch reverts the toggle to the server's stale value.
//
// This atom holds the user's chosen mode per session id until it can be replayed
// to the backend on reconnect (`useReplayPendingTranscribeMode`). It also backs
// the non-clobbering display guard in `useStartButtonLogic`, so a GET that races
// the reconnect replay can't flip the visible toggle before the PATCH lands.
//
// Intentionally in-memory: the toggle can be flipped before any offline
// recording record exists, so there's no offline DB row to persist to. The
// replay stays an explicit FE PATCH because this atom's sessions already have a
// server session id, and Rust sync skips session creation for those — not
// because the sync path can't send the mode (the chunked metadata PATCH does
// carry it; see `PatchSessionArgs` in `session_creation_engine/uploader.rs`).
// On an app restart the react-query cache that held the optimistic value is gone
// too, so there is no unsynced UI state left to protect.

import { atom, getDefaultStore } from 'jotai';

import { type TranscribeMode } from '@heidi/schema/ml-scribe';

/**
 * Map of `sessionId -> chosen TranscribeMode` for server sessions whose mode was
 * changed while offline and not yet persisted to the backend. Populated by
 * `useMutateUpdateSessionTranscribeModeById` (offline branch), drained by
 * `useReplayPendingTranscribeMode` on the reconnect edge, and read by
 * `useStartButtonLogic` as the display source of truth while an entry exists.
 */
export const pendingTranscribeModeBySessionIdAtom = atom<Record<string, TranscribeMode>>(
  {}
);

/**
 * Drop every deferred choice. Called from `clearLogoutStorage`.
 *
 * Same exposure as the consent store beside it, and fixed at the same time
 * rather than left as the leaking original: neither sign-out path reloads the
 * webview, so this map survives into the next account and the reconnect replay
 * would PATCH one clinician's toggle onto another clinician's session.
 */
export const clearPendingTranscribeMode = (): void => {
  getDefaultStore().set(pendingTranscribeModeBySessionIdAtom, {});
};
